import { faExclamationCircle, faUsersCrown } from '@fortawesome/pro-regular-svg-icons'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Column } from 'react-table'
import BuzzlUserDrawer from '../components/BuzzlUsers/BuzzlUserDrawer'
import BuzzlUserFilterDrawer from '../components/BuzzlUsers/BuzzlUserFilterDrawer'
import EmptyContent from '../components/EmptyContent'
import ErrorPage from '../components/ErrorPage'
import PageHeader from '../components/PageHeader'
import Table from '../components/Table'
import TableSkeleton from '../components/TableSkeleton'
import { useBuzzlUsers } from '../hooks/useBuzzlUsers'
import { useParamsToObj } from '../hooks/useParamsToObj'
import { BuzzlUser } from '../types/buzzl-user'

const BuzzlUsers: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const urlParams = useParamsToObj(['search', 'role', 'customer'])

  const { data, isError, isLoading } = useBuzzlUsers(urlParams)
  const [selectedUser, setSelectedUser] = useState<BuzzlUser>()
  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false)
  const [itemDrawerStatus, setItemDrawerStatus] = useState(false)

  useEffect(() => {
    if (!itemDrawerStatus) {
      setSelectedUser(undefined)
    }
  }, [itemDrawerStatus])

  const columns = React.useMemo<Column<BuzzlUser>[]>(
    () => [
      {
        Header: 'Name',
        accessor: ({ user }) =>
          user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'Not set yet!',
      },
      // {
      //   Header: 'First Name',
      //   accessor: ({ user }) => user?.firstName,
      // },
      // {
      //   Header: 'Surname',
      //   accessor: ({ user }) => user?.lastName,
      // },
      {
        Header: 'Email',
        accessor: ({ user }) => user?.email,
      },
      {
        Header: 'Assigned Customers',
        accessor: ({ assignedCustomers }) =>
          assignedCustomers && assignedCustomers.length > 0
            ? assignedCustomers.map(({ name }) => name).join(', ')
            : null,
      },
      {
        Header: 'Role',
        accessor: 'role',
      },
      {
        Header: 'Creation Date',
        accessor: ({ createdAt }) => new Date(createdAt).toLocaleDateString('en-GB'),
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: (buzzlUser) => (
          <button
            onClick={() => {
              setSelectedUser(buzzlUser)
              setItemDrawerStatus(true)
            }}
            className="text-link"
          >
            View
          </button>
        ),
      },
    ],
    []
  )

  if (isLoading) return <TableSkeleton headers={['Display Name', 'First Name', 'Surname', 'Email', ' ']} />

  if (isError || !data) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  if (data.length === 0 && !location.search) {
    return (
      <div className="mt-32 flex h-full items-center justify-center">
        <EmptyContent
          title="No Buzzjar Users"
          description="Start by adding a new one."
          icon={faUsersCrown}
          btnAction={() => setItemDrawerStatus(true)}
          btnText="Add Buzzjar User"
        />
      </div>
    )
  }

  return (
    <div>
      <BuzzlUserFilterDrawer open={filterDrawerStatus} onClose={() => setFilterDrawerStatus(false)} />
      <BuzzlUserDrawer open={itemDrawerStatus} onClose={() => setItemDrawerStatus(false)} selectedUser={selectedUser} />
      <PageHeader
        title="Buzzjar Users"
        btnText="Add User"
        btnAction={() => setItemDrawerStatus(!itemDrawerStatus)}
        filterBtnAction={() => setFilterDrawerStatus(true)}
        urlParams={urlParams}
        searchPlaceholder="Search Username"
      />
      {data.length > 0 ? (
        <Table columns={columns} data={data} />
      ) : (
        <div className="mt-32 flex h-full items-center justify-center">
          <EmptyContent
            title={`No results found`}
            description="Please change the search terms to match results."
            icon={faExclamationCircle}
            btnAction={() => {
              navigate({
                search: ``,
              })
            }}
            btnText="Reset filters"
          />
        </div>
      )}
    </div>
  )
}

export default BuzzlUsers
